// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Appointment, ChargeItem, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM } from '@polarissleep/shared';
import { useEffect, useState } from 'react';

const PAYABLE_APPOINTMENT_STATUSES = new Set<Appointment['status']>(['booked', 'pending']);
const UNPAID_CHARGE_STATUSES = new Set<ChargeItem['status']>(['planned', 'billable']);

function isFutureAppointment(appointment: Appointment, now: Date): boolean {
  const appointmentEndIso = appointment.end ?? appointment.start;
  if (!appointmentEndIso) {
    return false;
  }

  return new Date(appointmentEndIso).getTime() > now.getTime();
}

function getLinkedAppointmentId(chargeItem: ChargeItem): string | undefined {
  return chargeItem.identifier?.find((identifier) => identifier.system === APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM)
    ?.value;
}

export interface UseUpcomingPayableAppointmentsResult {
  appointments: Appointment[];
  loading: boolean;
}

export function useUpcomingPayableAppointments(): UseUpcomingPayableAppointmentsResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const patientId = profile?.id;
    if (!patientId) {
      setAppointments([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    Promise.all([
      medplum.searchResources('Appointment', {
        patient: `Patient/${patientId}`,
        _count: '100',
        _sort: 'date',
      }),
      medplum.searchResources('ChargeItem', {
        subject: `Patient/${patientId}`,
        _count: '100',
      }),
    ])
      .then(([appointmentResults, chargeResults]) => {
        const now = new Date();
        const unpaidChargeAppointmentIds = new Set(
          (chargeResults as ChargeItem[])
            .filter((chargeItem) => UNPAID_CHARGE_STATUSES.has(chargeItem.status))
            .map(getLinkedAppointmentId)
            .filter((appointmentId): appointmentId is string => Boolean(appointmentId))
        );

        const payableAppointments = (appointmentResults as Appointment[]).filter((appointment) => {
          if (
            !appointment.id ||
            !PAYABLE_APPOINTMENT_STATUSES.has(appointment.status) ||
            !isFutureAppointment(appointment, now)
          ) {
            return false;
          }

          return appointment.status === 'pending' || unpaidChargeAppointmentIds.has(appointment.id);
        });

        setAppointments(payableAppointments);
      })
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, [medplum, profile?.id]);

  return { appointments, loading };
}

// Backwards-compatible alias while callers move off the old pending-only name.
export type UseUpcomingPendingAppointmentsResult = UseUpcomingPayableAppointmentsResult;
export const useUpcomingPendingAppointments = useUpcomingPayableAppointments;
